import { defaultDateOptions } from "../../data/default-date-options.js";
import { defaultDateTimeOptions } from "../../data/default-date-time-options.js";
import { defaultDayOptions } from "../../data/default-day-options.js";
import { defaultMonthOptions } from "../../data/default-month-options.js";
import { defaultTimeOptions } from "../../data/default-time-options.js";
import { defaultWeekdayOptions } from "../../data/default-weekday-options.js";
import { defaultYearOptions } from "../../data/default-year-options.js";
import { getLocale } from "../shared-utils.js";
const defaultOptionTypes = ["date", "datetime", "day", "month", "time", "weekday", "year"];
const defaultOptionTypeToLocaleMap = {
    date: defaultDateOptions,
    datetime: defaultDateTimeOptions,
    day: defaultDayOptions,
    month: defaultMonthOptions,
    time: defaultTimeOptions,
    weekday: defaultWeekdayOptions,
    year: defaultYearOptions,
};
/**
 * Get locale-specific default options or generic default options
 *
 * @param {string} defaultOptionsTypesRef
 * @param {string | undefined} locale - {string | string[]} locale country code, BCP 47 language string, or array of either - default to 'en-US' if not set
 * @returns {undefined | Intl.DateTimeFormatOptions } standard dateTimeFormat options object
 */
const getDefaultLocaleOptions = (defaultOptionsTypesRef, locale) => {
    const localeForLookup = getLocale(locale);
    if (defaultOptionsTypesRef == null) {
        return undefined;
    }
    if (defaultOptionsTypesRef === "dateTime") {
        return getDefaultOptions(localeForLookup, "datetime");
    }
    // The original implementation of this function had a bug
    // so we're replicating it while we deprecate this function
    if (defaultOptionsTypesRef === "day") {
        return { dateStyle: "short", timeStyle: "short" };
    }
    return getDefaultOptions(localeForLookup, defaultOptionsTypesRef);
};
/**
 * Get recommended locale-specific options for common scenarios. These are just recommendations, you're always
 * welcome to pass custom options instead.
 *
 * @param locale locale country code, BCP 47 language string, or array of either - default to 'en-US' if not set
 * @param defaultOptionsType
 * @returns standard dateTimeFormat options object
 */
const getDefaultOptions = (locale, defaultOptionsType) => {
    const localeForLookup = getOverrideLookupKey(locale);
    if (!defaultOptionTypes.includes(defaultOptionsType)) {
        throw new Error(`Unexpected value "${defaultOptionsType}". Must be one of ${new Intl.ListFormat("en", {
            type: "disjunction",
        }).format(defaultOptionTypes)}.`);
    }
    const localeMap = defaultOptionTypeToLocaleMap[defaultOptionsType];
    if (localeMap == null) {
        return {};
    }
    const options = localeMap[localeForLookup] ?? localeMap["default"];
    return options;
};
function getOverrideLookupKey(locale) {
    const maximized = new Intl.Locale(locale).maximize();
    const { language, script } = maximized;
    const overrideDataKey = `${language}-${script}`;
    return overrideDataKey;
}
export { 
/**
 * @deprecated use getDefaultOptions
 */
defaultDateOptions, 
/**
 * @deprecated use getDefaultOptions
 */
defaultDateTimeOptions, 
/**
 * @deprecated use getDefaultOptions
 */
defaultMonthOptions, 
/**
 * @deprecated use getDefaultOptions
 */
defaultTimeOptions, 
/**
 * @deprecated use getDefaultOptions
 */
defaultWeekdayOptions, 
/**
 * @deprecated use getDefaultOptions
 */
defaultYearOptions, 
/**
 * @deprecated use getDefaultOptions
 */
getDefaultLocaleOptions, getDefaultOptions, };
